import React from "react";
import { Platform } from "react-native";
import { useTranslation } from "react-i18next";
import { Heading, FlexGrid } from "@clearabee/ui-library-native";
import { AddressField } from "components/Composite/AddressField/AddressField";
import { Input } from "components/Core/Input/Input";
import { styles } from "./DetailsForm.styles";
import { useFormContext } from "lib/hooks/useFormContext";
import { useAuth } from "lib/hooks/useAuth";
import { getValidationSchema } from "containers/ProductDetails/ProductDetails.validation";
import { z } from "zod";
import { showDeliveryText } from "lib/utils";
import { DisplayCoupon } from "components/DisplayCoupon/DisplayCoupon";

export interface DetailsFormProps {
  categorySku: string;
  disablePostcode?: boolean;
  initialManualMode?: boolean;
  coupon?: string;
}

export const DetailsForm = ({
  categorySku,
  disablePostcode = false,
  initialManualMode,
  coupon,
}: DetailsFormProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { user } = useAuth();
  const { Field } =
    useFormContext<z.infer<ReturnType<typeof getValidationSchema>>>();

  return (
    <FlexGrid
      style={{ flexDirection: "column" }}
      rowSpacing={null}
      wrap="nowrap"
    >
      <FlexGrid.Cell style={{ zIndex: 2 }} base="100%">
        <Heading
          level={5}
          styles={styles.heading}
          color={Platform.OS === "web" ? "accent" : "brand"}
        >
          {translate(
            showDeliveryText(categorySku)
              ? "hwa.screens.productDetails.deliveryDetails"
              : "hwa.screens.productDetails.collectionDetails",
          )}
        </Heading>
        <FlexGrid rowSpacing={null}>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.firstName"
              label={translate("common.firstName")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.firstName")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.lastName"
              label={translate("common.lastName")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  placeholder={translate("common.form.placeholder.lastName")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.email"
              label={translate("common.email")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.Text
                  {...field}
                  disabled={!!user}
                  autoCapitalize="none"
                  placeholder={translate(
                    "common.form.placeholder.emailAddress",
                  )}
                />
              )}
            </Field>
          </FlexGrid.Cell>
          <FlexGrid.Cell base="100%" large="50%">
            <Field
              name="contact.phoneNumber"
              label={translate("common.phoneNumber")}
              labelStyles={styles.label}
              errorStyles={styles.error}
              invertErrorColors
              errorBackground
            >
              {({ field }) => (
                <Input.InternationalPhone
                  {...field}
                  placeholder={translate("common.form.placeholder.phoneNumber")}
                />
              )}
            </Field>
          </FlexGrid.Cell>
        </FlexGrid>
      </FlexGrid.Cell>
      <FlexGrid.Cell base="100%">
        <FlexGrid rowSpacing={null}>
          <FlexGrid.Cell base="100%">
            <AddressField
              prefix="deliveryAddress"
              labelStyles={styles.label}
              errorStyles={styles.error}
              errorBackground
              invertErrorColors
              cellSpacing={Platform.OS !== "web" ? "xsmall" : undefined}
              disablePostcode={disablePostcode}
              initialManualMode={initialManualMode}
            />
          </FlexGrid.Cell>
        </FlexGrid>
      </FlexGrid.Cell>
      {!!coupon && (
        <FlexGrid.Cell base="100%">
          <FlexGrid
            rowSpacing={null}
            style={{
              justifyContent: "flex-end",
            }}
          >
            <FlexGrid.Cell base="100%" small="50%">
              <DisplayCoupon coupon={coupon} />
            </FlexGrid.Cell>
          </FlexGrid>
        </FlexGrid.Cell>
      )}
    </FlexGrid>
  );
};
